import FormSection from "../confComponents/FormSection";
import RazorpayGlyph from "@/features/razorpay/components/RazorpayGlyph";
import ContentWrapper from "../confComponents/ContentWrapper";
import GridContainer from "../confComponents/GridContainer";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  // --- Core Navigation (10) ---
  LayoutGrid, // Dashboard (Used for Dashboard)
  Home, // Home/Portal
  Settings, // Global Settings
  Users, // Users/Team (Used for Users & Leads)
  Building, // Organizations/Clients
  CreditCard, // Billing/Payments
  ChartArea, // Reports/Analytics
  Bell, // Notifications
  MessagesSquare, // Chat/Support
  History, // Activity Log
  // --- CRM/Education Workflow (10) ---
  UserPlus, // Enrollment/Add Lead (Used for Enrollment)
  Plane, // Visa/Travel (Used for Visa)
  FileText, // Applications/Documents (Used for Applications)
  GraduationCap, // University/Academics (Used for University)
  FlaskConical, // Stages/Processes (Used for Stages)
  Megaphone, // Sources/Campaigns (Used for Sources)
  FolderArchive, // Files/Resources
  Tags, // Products/Services
  Wallet, // Finances/Invoices
  Calendar, // Appointments/Deadlines
  // --- Data & Customization (10) ---
  SlidersHorizontal, // Custom Fields/Filters (Used for Custom)
  ListOrdered, // Tasks/To-Do
  Kanban, // Projects/Workflows
  Table, // Data Grid/View
  Lock, // Security/Permissions
  Send, // Outbound/Emails
  Cloud, // Storage/Backups
  Code, // Developer/API
  Globe, // Locales/Regions
  Monitor, // Appearance/Theme
  // --- Communication & Tools (10) ---
  Mail, // Email Templates
  Phone, // Calls/Contacts
  Download, // Export Data
  Upload, // Import Data
  Link, // Integrations
  ShoppingCart, // Store/Marketplace
  Mic, // Voice/Audio Settings
  Scissors, // Cut/Trim (alternative for process)
  Pencil, // Editor/Drafts
  BookOpen, // Documentation/Help
  // --- Miscellaneous & Advanced (10) ---
  Clock, // Time Tracking
  MapPin, // Locations/Addresses
  Zap, // Automations
  Shield, // Compliance/Legal
  Key, // API Keys
  Gift, // Rewards/Referrals
  Star, // Favorites/Bookmarks
  Map, // Geography/Territory
  MousePointer, // Clicks/Interactions
  Heart,
  LayoutDashboard,
  GitBranch,
  UserCog,
  Cpu, // Status/Health Check
  BarChart3, // Report/Analytics tab
} from "lucide-react";
import * as LucideIcons from "lucide-react";

/** Public `public/assets/*` — matches Lucide usage (`<Icon className="..." />`) in the sidebar. */
function routeTabAssetIcon(src: string) {
  function Icon({ className }: { className?: string }) {
    return (
      <span
        className={cn(
          "inline-flex size-5 shrink-0 items-center justify-center [&>img]:size-5 [&>img]:object-contain",
          className,
        )}
      >
        <img src={src} alt="" loading="lazy" decoding="async" />
      </span>
    );
  }
  return Icon;
}

const tabAsset = (file: string) =>
  routeTabAssetIcon(
    `${import.meta.env.BASE_URL}assets/${file}`.replace(/\/{2,}/g, "/"),
  );

// Curated base set (named imports above).
const baseIconMap = {
  // Core Navigation
  LayoutGrid,
  Home,
  Settings,
  Users,
  Building,
  CreditCard,
  ChartArea,
  Bell,
  MessagesSquare,
  History,
  // CRM/Education Workflow
  UserPlus,
  Plane,
  FileText,
  GraduationCap,
  FlaskConical,
  Megaphone,
  FolderArchive,
  Tags,
  Wallet,
  Calendar,
  // Data & Customization
  SlidersHorizontal,
  ListOrdered,
  Kanban,
  Table,
  Lock,
  Send,
  Cloud,
  Code,
  Globe,
  Monitor,
  // Communication & Tools
  Mail,
  Phone,
  Download,
  Upload,
  Link,
  ShoppingCart,
  Mic,
  Scissors,
  Pencil,
  BookOpen,
  // Miscellaneous & Advanced
  Clock,
  MapPin,
  Zap,
  Shield,
  Key,
  Gift,
  Star,
  Map,
  MousePointer,
  Heart,
};

// Extra icons useful for admissions/education pipelines — outcomes, documents,
// finance, travel, people. Resolved from the lucide namespace and GUARDED, so a
// name absent in the installed lucide version is simply skipped (never breaks
// the build). Stored value is the icon NAME, resolved the same way elsewhere
// via `resolveIcon` / `LucideIcons[name]`.
const EXTRA_ICON_NAMES = [
  // Outcome / status
  "CircleCheck", "CircleX", "CircleAlert", "CircleHelp", "BadgeCheck",
  "ThumbsUp", "Flag", "Target", "Milestone",
  // Time / pending / retry
  "Hourglass", "Timer", "AlarmClock", "CalendarCheck", "CalendarClock",
  "RefreshCw", "RotateCcw",
  // Documents
  "ClipboardCheck", "ClipboardList", "FileCheck", "FileSignature", "FileSearch",
  "ScrollText", "NotebookPen", "Paperclip", "Stamp", "Inbox",
  // Education
  "Award", "Trophy", "Medal", "School", "Library", "BookMarked", "Languages",
  // People
  "UserCheck", "UserX", "UserRound", "Contact", "Handshake", "HeartHandshake",
  // Money
  "Banknote", "DollarSign", "Coins", "Receipt", "PiggyBank",
  // Travel / visa
  "PlaneTakeoff", "PlaneLanding", "Luggage", "Ticket",
  // Communication
  "Video", "PhoneCall", "AtSign", "BellRing",
  // Process / misc
  "Workflow", "Sparkles", "Rocket", "TrendingUp", "Activity", "Eye", "Search",
  "Filter", "Pin", "Bookmark", "Briefcase", "ShieldCheck", "KeyRound", "Info",
] as const;

const extraIconMap = Object.fromEntries(
  EXTRA_ICON_NAMES.filter(
    (name) => (LucideIcons as any)[name] && !(name in baseIconMap),
  ).map((name) => [name, (LucideIcons as any)[name]]),
);

// Base curated set first, then the extras.
const iconMap = { ...baseIconMap, ...extraIconMap };

const iconEntries = Object.entries(iconMap);

export default function TheIconSelector({
  icon,
  setIcon,
}: {
  icon: string;
  setIcon: any;
}) {
  return (
    <FormSection title="Icon">
      <ContentWrapper className="bg-transparent">
        <GridContainer columns={6}>
          {iconEntries.map(([iconName, IconComponent]) => {
            const isSelected = iconName === icon;

            return (
              <Button
                key={iconName}
                onClick={() => setIcon(iconName)}
                // **1. Remove default variant for selected state**
                // We'll handle the selected appearance entirely with custom styles/style prop
                variant="outline"
                className={`w-full aspect-square p-2 ${
                  // **2. Add styling for a selected button outline (if still desired)**
                  isSelected ? "hover:bg-transparent" : ""
                }`}
                // **3. Conditionally apply the gradient background via the style prop**
                style={
                  isSelected
                    ? {
                        backgroundImage: "var(--heading-gradient)",
                        // Ensure the text and icon are readable over the dark gradient
                        color: "white",
                        borderColor: "transparent",
                      }
                    : {}
                }
                aria-pressed={isSelected}
              >
                {/* 4. Adjust icon color based on selection */}
                <IconComponent
                  className={`w-5 h-5 ${
                    isSelected ? "text-white" : "text-var(--color-black-5)"
                  }`}
                  // If you use the 'style' prop on the Button to set 'color: white',
                  // the icon might inherit it. If not, this explicit class is needed.
                />
              </Button>
            );
          })}
        </GridContainer>
      </ContentWrapper>
    </FormSection>
  );
}

export const TAB_ICON_MAP: Record<string, any> = {
  // CORE / STAGES
  dashboard: LayoutDashboard,
  leads: Users,
  whatsapp: tabAsset("whatsapp.png"),
  gmail: tabAsset("gmail.png"),

  // Dynamic stages (fallback)
  stage: FileText,

  // ACADEMICS (NICHE)
  universities: GraduationCap,

  // INTEGRATION (connected tools with their own page)
  razorpay: RazorpayGlyph,
  tools: LucideIcons.Plug,

  // CONFIGURATIONS
  "stage-management": GitBranch,
  "user-management": UserCog,
  "source-management": Megaphone,
  "custom-field-management": SlidersHorizontal,
  automation: Cpu,
  settings: Settings,
  report: BarChart3,
};
export function resolveIcon(route: { key: string; iconName?: string }) {
  // 1. Try the hardcoded mapping first (for specific overrides)
  if (TAB_ICON_MAP[route.key]) {
    return TAB_ICON_MAP[route.key];
  }

  // 2. Try the dynamic iconName prop
  if (route.iconName && (LucideIcons as any)[route.iconName]) {
    return (LucideIcons as any)[route.iconName];
  }
  // 3. Last resort fallback
  return LucideIcons.FileText;
}
